import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { LargeText, RegularText } from '@utils/typography';
import colors from '@utils/colors';
import { AuthHeading } from '../../auth/partials';
import t from '@i18n/t';
import maintenanceImage from '@assets/images/maintenance-page.png';
import Loader from 'react-loader-spinner';
import { backendUp, leaveMaintenancePage, plannedMaintenance, SPINNER_MIN_MS } from '@utils/maintenance';

const CHECK_MS = 10_000;

const MaintenancePage = () => {
  // Takes the student back to their page as soon as Shovel answers; not during planned maintenance.
  useEffect(() => {
    if (plannedMaintenance) return;
    let stopped = false;
    let timeout: number | undefined;
    const check = async () => {
      if (await backendUp()) {
        if (!stopped) leaveMaintenancePage();
        return;
      }
      if (!stopped) timeout = window.setTimeout(check, CHECK_MS);
    };
    check();
    return () => {
      stopped = true;
      window.clearTimeout(timeout);
    };
  }, []);

  // "Try again" asks first: while Shovel is still down it stays here instead of opening a page that can't load. A
  // spinner stands in for it meanwhile, then the button comes back.
  const [reloading, setReloading] = useState(false);
  const reload = async () => {
    if (reloading) return;
    setReloading(true);
    const [up] = await Promise.all([backendUp(), new Promise(resolve => setTimeout(resolve, SPINNER_MIN_MS))]);
    if (up) leaveMaintenancePage();
    else setReloading(false);
  };

  return (
    <Container>
      {/* "Scheduled maintenance" only when it is; otherwise Shovel just isn't answering. */}
      <AuthHeading title={plannedMaintenance ? t.MAINTENANCE_TITLE : t.NOT_RESPONDING_TITLE} />
      <MessageWrapper>
        <Message>{plannedMaintenance ? t.MAINTENANCE_TEXT : t.NOT_RESPONDING_TEXT}</Message>
      </MessageWrapper>
      <TryAgainLink checking={reloading} onClick={reload} />
      <img src={maintenanceImage} width={610} />
    </Container>
  );
};

export default MaintenancePage;

/** The button of the maintenance and offline pages: a spinner while it checks. */
export const TryAgainLink = ({ checking, onClick }: { checking: boolean; onClick: () => void }) => (
  <ReloadLink onClick={onClick} style={checking ? { cursor: 'progress' } : undefined}>
    {checking ? <Loader type="Oval" color={colors.primaryPurple} height={22} width={22} /> : t.TRY_AGAIN}
  </ReloadLink>
);

export const Container = styled.div`
  flex-direction: column;
  display: flex;
  flex: 1;
  padding-top: 115px;
  padding-bottom: 30px;
  align-items: center;
  overflow: auto;
`;

export const MessageWrapper = styled.div`
  margin-bottom: 20px;
`;

export const Message = styled(LargeText)`
  max-width: 468px;
  margin-bottom: 30px;

  a {
    text-decoration: none;
    color: ${colors.primaryPurple};
  }
`;

const ReloadLink = styled(LargeText)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  color: ${colors.primaryPurple};
  cursor: pointer;
  margin-bottom: 40px;
`;
